import i18n from "../../../i18n";
import PageCard from "../page-card";
import styles from "./InstitutionInformation.module.css";
import DemoCourseIcon from "../../../images/settings/demo-course.svg?react";
import { CoreDatePicker, CoreMenuItem, CoreSelectTextField, CoreSwitch } from "@verity/components/core";
import { InfoRow } from "./InstitutionInformation";
import { Trans } from "react-i18next";
import { Typography } from "@mui/material";

export const CLEANUP_FREQUENCY = ["daily", "weekly", "monthly"] as const;
export type CleanupFrequency = (typeof CLEANUP_FREQUENCY)[number];

function getNextCleanupDate(cutoffDate: Date, frequency: CleanupFrequency): Date {
	const now = new Date();

	// 1. Initialize nextRun to 5:00 AM UTC on the cutoff date
	const nextRun = new Date(Date.UTC(cutoffDate.getFullYear(), cutoffDate.getMonth(), cutoffDate.getDate(), 5));
	nextRun.setUTCHours(5, 0, 0, 0);

	// 2. Step forward until we find the first available date in the future.
	const anchorDay = nextRun.getUTCDate();

	while (nextRun.getTime() <= now.getTime()) {
		if (frequency === "daily") {
			nextRun.setUTCDate(nextRun.getUTCDate() + 1);
		} else if (frequency === "weekly") {
			nextRun.setUTCDate(nextRun.getUTCDate() + 7);
		} else if (frequency === "monthly") {
			// Move to 1st of next month
			nextRun.setUTCMonth(nextRun.getUTCMonth() + 1, 1);

			// Handle month-end clamping (e.g., Jan 31 -> Feb 28)
			const lastDay = new Date(Date.UTC(nextRun.getUTCFullYear(), nextRun.getUTCMonth() + 1, 0)).getUTCDate();
			nextRun.setUTCDate(Math.min(anchorDay, lastDay));
		}
	}

	return nextRun;
}

type DemoCourseCardProps = {
	loading: boolean;
	cutoffDate: Date;
	setCutoffDate: (newDate: Date) => void;
	cleanupFrequency: CleanupFrequency;
	setCleanupFrequency: (newFreq: CleanupFrequency) => void;
	cleanupEnabled: boolean;
	setCleanupEnabled: (enabled: boolean) => void;
};

export default function DemoCourseCard({
	cleanupEnabled,
	cleanupFrequency,
	cutoffDate,
	loading,
	setCleanupEnabled,
	setCleanupFrequency,
	setCutoffDate,
}: DemoCourseCardProps) {
	// Extracted out and asserted type because typescript was complaining.
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
	const selectedFreqText = i18n.t(`portal.components.settings.demoCourseTSX.frequency.${cleanupFrequency}`) as string;
	return (
		<div className={styles.pageCardWrapper}>
			<PageCard
				icon={<DemoCourseIcon />}
				title={i18n.t("portal.components.settings.demoCourseTSX.header")}
				titleVariant="h2"
				className={styles.pageCard}
			>
				<div className={styles.pageCardBody}>
					<table className={styles.infoTable} role="none">
						{cleanupEnabled && (
							<tr>
								<td colSpan={2}>
									<div className={styles.demoBanner}>
										<Typography className={styles.title}>
											{i18n.t("portal.components.settings.demoCourseTSX.cleanupSummary")}
										</Typography>
										<Typography>
											{i18n.t("portal.components.settings.demoCourseTSX.cleanupSummaryDescription", {
												freq: selectedFreqText.toLocaleLowerCase(),
												localizedDate: new Intl.DateTimeFormat(i18n.language, {
													month: "short",
													day: "numeric",
													year: "numeric",
												}).format(cutoffDate),
											})}
										</Typography>
										<Typography className={styles.title}>
											{i18n.t("portal.components.settings.demoCourseTSX.nextCleanup")}
										</Typography>
										<Typography>
											{i18n.t("portal.components.settings.demoCourseTSX.nextCleanupDate", {
												localizedDate: new Intl.DateTimeFormat(i18n.language, {
													month: "short",
													day: "numeric",
													year: "numeric",
												}).format(getNextCleanupDate(cutoffDate, cleanupFrequency)),
											})}
										</Typography>
									</div>
								</td>
							</tr>
						)}
						<InfoRow
							header={i18n.t("portal.components.settings.demoCourseTSX.enableCleanup")}
							description={i18n.t("portal.components.settings.demoCourseTSX.enableCleanupDescription")}
							helperText={i18n.t("portal.components.settings.demoCourseTSX.enableCleanupHint")}
							createControl={(id) => (
								<CoreSwitch
									id={id}
									checked={cleanupEnabled}
									labelTextType="left"
									onChange={(_, checked) => setCleanupEnabled(checked)}
								/>
							)}
							isLoading={loading}
						/>
						{cleanupEnabled && (
							<>
								<InfoRow
									header={i18n.t("portal.components.settings.demoCourseTSX.cutoffTime")}
									description={i18n.t("portal.components.settings.demoCourseTSX.cutoffTimeDescription")}
									helperText={
										<Trans
											i18n={i18n}
											i18nKey={"portal.components.settings.demoCourseTSX.cutoffTimeHint"}
											components={{ b: <strong /> }}
										/>
									}
									createControl={(id) => (
										<CoreDatePicker
											value={cutoffDate}
											onChange={(date) => {
												const selectedDate = date ?? new Date();
												selectedDate.setHours(23, 59, 59, 999);
												setCutoffDate(selectedDate);
											}}
											slotProps={{
												textField: { id, InputProps: { sx: { width: 165 } } },
											}}
											maxDate={new Date()}
											useButton
										/>
									)}
									isLoading={loading}
								/>
								<InfoRow
									header={i18n.t("portal.components.settings.demoCourseTSX.cleanupSchedule")}
									description={i18n.t("portal.components.settings.demoCourseTSX.cleanupScheduleDescription")}
									helperText={i18n.t("portal.components.settings.demoCourseTSX.cleanupScheduleHint")}
									createControl={(id) => (
										<CoreSelectTextField<CleanupFrequency>
											id={id}
											value={cleanupFrequency}
											onChange={(e) => {
												const newFreq = e.target.value as CleanupFrequency;
												setCleanupFrequency(newFreq);
											}}
											label={i18n.t("portal.components.settings.demoCourseTSX.frequency.label")}
											useFloatingLabel
											renderValue={(value) => (
												<span>{i18n.t(`portal.components.settings.demoCourseTSX.frequency.${value}`)}</span>
											)}
										>
											{CLEANUP_FREQUENCY.map((freq) => (
												<CoreMenuItem key={freq} value={freq}>
													{i18n.t(`portal.components.settings.demoCourseTSX.frequency.${freq}`)}
												</CoreMenuItem>
											))}
										</CoreSelectTextField>
									)}
									isLoading={loading}
								/>
							</>
						)}
					</table>
				</div>
			</PageCard>
		</div>
	);
}

export function normalizeCutoffDate(date: Date) {
	// 1. Extract the local Year, Month, and Day the user actually selected
	const year = date.getFullYear();
	const month = date.getMonth();
	const day = date.getDate();

	// 2. Create a new UTC-based date using those specific calendar numbers
	// This sets the time to 23:59:59 UTC on the chosen day
	const normalizedDate = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));

	return normalizedDate.toISOString();
}
